import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Hello Clio's two cart gifts, counted on Hello Clio items only so no Shopise product unlocks them.
 *
 * - Mystery Pin: added once Hello Clio items reach the threshold, removed when they drop below it.
 *   A shopper who takes it out while still over the threshold keeps it out for the session.
 * - Extended strap: one stays in the cart while a Hello Clio belt bag is there. Without a belt bag,
 *   or beyond the first one, it is taken out.
 *
 * The products come from Hello Clio through Shopify Collective. Their ids are resolved by handle in
 * snippets/hello-clio-gifts.liquid, so a re-import keeps this working.
 */

const SOURCE = 'hello-clio-gifts';
const DECLINED_KEY = 'hello-clio-pin-declined';

const configElement = document.getElementById('hello-clio-gifts-config');
const config = configElement ? JSON.parse(configElement.textContent) : null;
const cartRoot = window.Shopify?.routes?.root ?? '/';

/** @type {any} */
let lastCart = null;
let running = false;
let queued = false;
/** @type {number | undefined} */
let scheduled;

/**
 * @param {string} path
 * @param {Object} [body]
 */
async function cartRequest(path, body) {
  const init = body
    ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }
    : { headers: { Accept: 'application/json' } };
  const response = await fetch(`${cartRoot}${path}`, init);
  if (!response.ok) throw new Error(`${path} returned ${response.status}`);
  return response.json();
}

const fetchCart = () => cartRequest('cart.js');

function isDeclined() {
  try {
    return sessionStorage.getItem(DECLINED_KEY) === '1';
  } catch {
    return false;
  }
}

function setDeclined() {
  try {
    sessionStorage.setItem(DECLINED_KEY, '1');
  } catch {}
}

/** @param {any} item */
const isPin = (item) => item.product_id === config.pinProductId;
/** @param {any} item */
const isStrap = (item) => item.product_id === config.strapProductId;
/** @param {any} item */
const isBeltBag = (item) => config.beltBagProductIds.includes(item.product_id);

/**
 * What the shopper pays for Hello Clio items after discounts, leaving out the gifts themselves.
 * @param {any} cart
 */
function helloClioSpend(cart) {
  return cart.items.reduce(
    (total, item) => (item.vendor === config.vendor && !isPin(item) && !isStrap(item) ? total + item.final_line_price : total),
    0
  );
}

/**
 * The changes the gift rules call for: an `updates` map for /cart/update.js and items for /cart/add.js.
 * @param {any} cart
 */
function plan(cart) {
  /** @type {Record<string, number>} */
  const updates = {};
  const adds = [];
  const spend = helloClioSpend(cart);

  if (config.pinProductId) {
    const pinLine = cart.items.find(isPin);
    if (spend >= config.pinThresholdCents) {
      if (!pinLine && !isDeclined() && config.pinVariantId) adds.push({ id: config.pinVariantId, quantity: 1 });
      else if (pinLine && pinLine.quantity > 1) updates[pinLine.key] = 1;
    } else if (pinLine) {
      updates[pinLine.key] = 0;
    }
  }

  if (config.strapProductId) {
    const hasBeltBag = cart.items.some(isBeltBag);
    cart.items.filter(isStrap).forEach((line, index) => {
      const keep = hasBeltBag && index === 0 ? 1 : 0;
      if (line.quantity !== keep) updates[line.key] = keep;
    });
  }

  return { updates, adds };
}

/** @param {any} cart */
function announce(cart) {
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count }));
}

async function run() {
  if (!config) return;
  if (running) {
    queued = true;
    return;
  }
  running = true;

  try {
    let cart = await fetchCart();

    // The pin was there last time and is gone now while the cart still qualifies: the shopper took it out.
    if (lastCart?.items.some(isPin) && !cart.items.some(isPin) && helloClioSpend(cart) >= config.pinThresholdCents) {
      setDeclined();
    }

    // A second pass catches a change that only shows up once the first one lands.
    for (let pass = 0; pass < 2; pass++) {
      const { updates, adds } = plan(cart);
      if (!Object.keys(updates).length && !adds.length) break;
      if (Object.keys(updates).length) cart = await cartRequest('cart/update.js', { updates });
      if (adds.length) {
        await cartRequest('cart/add.js', { items: adds });
        cart = await fetchCart();
      }
      announce(cart);
    }

    lastCart = cart;
  } catch (error) {
    console.warn('[hello-clio-gifts]', error);
  } finally {
    running = false;
    if (queued) {
      queued = false;
      run();
    }
  }
}

function schedule() {
  clearTimeout(scheduled);
  scheduled = window.setTimeout(run, 150);
}

/**
 * The free-strap colour picker on Hello Clio belt bag pages. Adds the chosen strap, and the bag
 * selected in the product form too when no belt bag is in the cart yet, so the strap never lands
 * on its own and gets taken straight back out.
 */
class HelloClioFreeStrap extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#add);
  }

  #add = async () => {
    const button = /** @type {HTMLButtonElement} */ (this.querySelector('button'));
    const select = /** @type {HTMLSelectElement} */ (this.querySelector('select'));
    const status = this.querySelector('[role="status"]');
    if (!config || !button || !select) return;

    button.disabled = true;
    try {
      const cart = await fetchCart();
      const items = [];

      if (!cart.items.some(isBeltBag)) {
        const form = document.getElementById(this.dataset.productFormId ?? '');
        const bagVariantId = Number(form?.querySelector('input[name="id"]')?.getAttribute('value'));
        if (bagVariantId) items.push({ id: bagVariantId, quantity: 1 });
      }
      items.push({ id: Number(select.value), quantity: 1 });

      // Choosing another colour swaps the strap rather than adding a second one.
      const strapLines = cart.items.filter(isStrap);
      if (strapLines.length) {
        await cartRequest('cart/update.js', {
          updates: Object.fromEntries(strapLines.map((/** @type {any} */ line) => [line.key, 0])),
        });
      }
      await cartRequest('cart/add.js', { items });

      const updated = await fetchCart();
      lastCart = updated;
      document.dispatchEvent(new CartAddEvent(updated, SOURCE, { source: SOURCE, itemCount: updated.item_count }));
      if (status) status.textContent = 'Added to your cart.';
      schedule();
    } catch (error) {
      console.warn('[hello-clio-gifts]', error);
      if (status) status.textContent = 'The strap could not be added. Try again.';
    } finally {
      button.disabled = false;
    }
  };
}

if (config) {
  if (!customElements.get('hello-clio-free-strap')) customElements.define('hello-clio-free-strap', HelloClioFreeStrap);

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (/** @type {any} */ (event).detail?.sourceId === SOURCE) return;
    schedule();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule();
  });

  run();
}
